import { useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, Send, Phone, MapPin, Navigation, Clock, KeyRound } from "lucide-react";
import { Reveal } from "./Reveal";
import { SERVICES, BUSINESS, BUSINESS_HOURS, DIRECTIONS_URL } from "../config/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "8:30 AM", "9:30 AM", "10:30 AM", "11:30 AM",
  "12:30 PM", "1:30 PM", "2:30 PM", "3:30 PM", "Any time",
];

const INITIAL = {
  full_name: "", phone: "", email: "",
  vehicle_year: "", vehicle_make: "", vehicle_model: "",
  service_needed: "", preferred_date: "", preferred_time: "", message: "",
};

const validate = (f) => {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
  if (!/^[+()\-.\s\d]{7,}$/.test(f.phone.trim())) e.phone = "Please enter a valid phone number.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Please enter a valid email address.";
  const yr = parseInt(f.vehicle_year, 10);
  if (!/^\d{4}$/.test(f.vehicle_year) || yr < 1950 || yr > new Date().getFullYear() + 1)
    e.vehicle_year = "Enter a 4-digit year (e.g. 2018).";
  if (!f.vehicle_make.trim()) e.vehicle_make = "Vehicle make is required.";
  if (!f.vehicle_model.trim()) e.vehicle_model = "Vehicle model is required.";
  if (!f.service_needed) e.service_needed = "Please select a service.";
  if (!f.preferred_date) e.preferred_date = "Please choose a preferred date.";
  if (!f.preferred_time) e.preferred_time = "Please choose a preferred time.";
  return e;
};

const Field = ({ label, error, children, htmlFor }) => (
  <div>
    <label
      htmlFor={htmlFor}
      className="font-display mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#a6a9b1]"
    >
      {label}
    </label>
    {children}
    {error && (
      <p data-testid={`error-${htmlFor}`} className="mt-1.5 flex items-center gap-1.5 text-sm text-[#ff6b6b]" role="alert">
        <AlertCircle className="h-3.5 w-3.5" /> {error}
      </p>
    )}
  </div>
);

const ContactSidebar = () => (
  <aside className="flex h-full flex-col border border-white/10 bg-[#141519] p-7">
    <h3 className="font-display text-xl font-bold uppercase tracking-wide text-[#f4f4f2]">
      Contact Information
    </h3>
    <p className="font-display mt-5 text-sm font-bold uppercase tracking-[0.2em] text-[#00AEEF]">
      {BUSINESS.name}
    </p>
    <p className="mt-2 flex items-start gap-2.5 text-sm text-[#c9ccd2]">
      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#00AEEF]" />
      <span>
        {BUSINESS.addressLines.map((l) => (
          <span key={l} className="block">{l}</span>
        ))}
      </span>
    </p>
    <a
      href={BUSINESS.phoneHref}
      data-testid="appointment-sidebar-phone"
      className="mt-3 flex items-center gap-2.5 font-semibold text-[#c9ccd2] transition-colors hover:text-white"
    >
      <Phone className="h-4 w-4 shrink-0 text-[#00AEEF]" />
      {BUSINESS.phone}
    </a>
    <a
      data-testid="appointment-directions-button"
      href={DIRECTIONS_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="btn-primary mt-6 w-full !text-sm"
    >
      <Navigation className="h-4 w-4" />
      Get Directions
    </a>

    <h4 className="font-display mt-8 flex items-center gap-2 text-base font-bold uppercase tracking-wide text-[#f4f4f2]">
      <Clock className="h-4 w-4 text-[#00AEEF]" />
      Business Hours
    </h4>
    <dl className="mt-4" data-testid="business-hours">
      {BUSINESS_HOURS.map((h) => (
        <div
          key={h.day}
          className="flex items-center justify-between border-b border-white/5 py-2 text-sm last:border-0"
        >
          <dt className="text-[#c9ccd2]">{h.day}</dt>
          <dd className={h.closed ? "font-semibold text-[#00AEEF]" : "text-[#a6a9b1]"}>
            {h.closed ? "Closed" : `${h.open} – ${h.close}`}
          </dd>
        </div>
      ))}
    </dl>

    <div className="mt-7 border border-[#00AEEF]/30 bg-[#00AEEF]/5 p-5" data-testid="key-drop-info">
      <p className="font-display flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#f4f4f2]">
        <KeyRound className="h-4 w-4 shrink-0 text-[#00AEEF]" />
        After-Hours Key Drop Available
      </p>
      <p className="mt-2 text-xs leading-relaxed text-[#a6a9b1]">
        Busy schedule? For scheduled appointments, leave your vehicle at the
        shop and use our designated key drop box. We'll take it from there when
        the shop opens.
      </p>
    </div>
  </aside>
);

export const AppointmentForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState("");

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    setApiError("");
    try {
      await axios.post(`${API}/appointments`, form);
      setSuccess(true);
    } catch {
      setApiError("Something went wrong sending your request. Please call us at (905) 206-1276 instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const errCls = (k) => (errors[k] ? "field field-error" : "field");

  return (
    <section id="appointment" className="spotlight relative bg-[#111214] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-[#00AEEF] sm:text-sm">
            Book Service
          </p>
          <h2 className="font-display mt-3 text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-[#f4f4f2] sm:text-4xl lg:text-5xl">
            Request an Appointment
          </h2>
          <p className="mt-5 max-w-2xl leading-relaxed text-[#a6a9b1]">
            Submitting this form sends an appointment{" "}
            <strong className="text-[#f4f4f2]">request</strong>. Your
            appointment is not confirmed until Auto Trust Centre contacts you
            to confirm the date and time.
          </p>
        </Reveal>

        <div className="mt-12 grid items-start gap-8 lg:grid-cols-3">
          <Reveal className="lg:col-span-2">
            {success ? (
              <div
                data-testid="appointment-success"
                className="border border-[#00AEEF]/50 bg-[#141519] p-10 text-center lg:p-14"
                role="status"
              >
                <CheckCircle2 className="mx-auto h-14 w-14 text-[#00AEEF]" strokeWidth={1.5} />
                <h3 className="font-display mt-5 text-3xl font-bold uppercase text-[#f4f4f2]">
                  Request Received
                </h3>
                <p className="mx-auto mt-3 max-w-md leading-relaxed text-[#c9ccd2]">
                  Thank you, {form.full_name.split(" ")[0]}. We've received your
                  appointment request for your {form.vehicle_year}{" "}
                  {form.vehicle_make} {form.vehicle_model}. Auto Trust Centre will
                  contact you shortly to confirm your appointment.
                </p>
                <button
                  data-testid="appointment-another-button"
                  onClick={() => { setForm(INITIAL); setSuccess(false); }}
                  className="btn-ghost mt-8"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form
                data-testid="appointment-form"
                onSubmit={submit}
                noValidate
                className="border border-white/10 bg-[#141519] p-6 sm:p-8"
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label="Full Name *" error={errors.full_name} htmlFor="full_name">
                    <input id="full_name" data-testid="input-full-name" className={errCls("full_name")} value={form.full_name} onChange={set("full_name")} placeholder="Jane Smith" autoComplete="name" />
                  </Field>
                  <Field label="Phone Number *" error={errors.phone} htmlFor="phone">
                    <input id="phone" data-testid="input-phone" type="tel" className={errCls("phone")} value={form.phone} onChange={set("phone")} placeholder="(905) 555-0123" autoComplete="tel" />
                  </Field>
                  <Field label="Email *" error={errors.email} htmlFor="email">
                    <input id="email" data-testid="input-email" type="email" className={errCls("email")} value={form.email} onChange={set("email")} placeholder="you@example.com" autoComplete="email" />
                  </Field>
                  <Field label="Service Needed *" error={errors.service_needed} htmlFor="service_needed">
                    <select id="service_needed" data-testid="select-service" className={errCls("service_needed")} value={form.service_needed} onChange={set("service_needed")}>
                      <option value="">Select a service…</option>
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.title}>{s.title}</option>
                      ))}
                      <option value="Not sure / Other">Not sure / Other</option>
                    </select>
                  </Field>
                  <Field label="Vehicle Year *" error={errors.vehicle_year} htmlFor="vehicle_year">
                    <input id="vehicle_year" data-testid="input-vehicle-year" inputMode="numeric" maxLength={4} className={errCls("vehicle_year")} value={form.vehicle_year} onChange={set("vehicle_year")} placeholder="2018" />
                  </Field>
                  <Field label="Vehicle Make *" error={errors.vehicle_make} htmlFor="vehicle_make">
                    <input id="vehicle_make" data-testid="input-vehicle-make" className={errCls("vehicle_make")} value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="Honda" />
                  </Field>
                  <Field label="Vehicle Model *" error={errors.vehicle_model} htmlFor="vehicle_model">
                    <input id="vehicle_model" data-testid="input-vehicle-model" className={errCls("vehicle_model")} value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="Civic" />
                  </Field>
                  <div className="grid grid-cols-2 gap-6">
                    <Field label="Preferred Date *" error={errors.preferred_date} htmlFor="preferred_date">
                      <input id="preferred_date" data-testid="input-preferred-date" type="date" className={errCls("preferred_date")} value={form.preferred_date} onChange={set("preferred_date")} min={new Date().toISOString().split("T")[0]} />
                    </Field>
                    <Field label="Preferred Time *" error={errors.preferred_time} htmlFor="preferred_time">
                      <select id="preferred_time" data-testid="select-preferred-time" className={errCls("preferred_time")} value={form.preferred_time} onChange={set("preferred_time")}>
                        <option value="">Select…</option>
                        {TIME_SLOTS.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Message / Describe the Problem" error={errors.message} htmlFor="message">
                      <textarea id="message" data-testid="input-message" rows={4} className={errCls("message")} value={form.message} onChange={set("message")} placeholder="Tell us what you're noticing — sounds, warning lights, when it happens…" />
                    </Field>
                  </div>
                </div>

                {apiError && (
                  <p data-testid="appointment-api-error" className="mt-6 flex items-center gap-2 border border-[#ff6b6b]/50 bg-[#ff6b6b]/10 p-4 text-sm text-[#ffb3b3]" role="alert">
                    <AlertCircle className="h-4 w-4 shrink-0" /> {apiError}
                  </p>
                )}

                <button
                  data-testid="appointment-submit-button"
                  type="submit"
                  disabled={submitting}
                  className="btn-primary mt-8 w-full disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Send className="h-5 w-5" />
                  {submitting ? "Sending Request…" : "Request Appointment"}
                </button>
                <p className="mt-4 text-center text-xs text-[#6b6e76]">
                  This is a request only. Appointment is not confirmed until we contact you.
                </p>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.1}>
            <ContactSidebar />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
